/**
 * Lists the CLIENT owns, because they are published standards rather than
 * instrument content (config decision #17, and config's reply on mail #62).
 *
 * Country: config deliberately ships no options and stores the ISO 3166-1
 * alpha-2 code. Falling back to crisis_resources.countries would have been
 * wrong — it holds only the four countries that have resource entries, so a
 * participant in France could not have registered at all.
 *
 * Neither list is hand-typed here. Both are derived from the platform, so a
 * wrong code or a stale name is not something this file can introduce.
 */

/**
 * Exceptionally-reserved and aggregate codes ICU will happily name but which
 * are not ISO 3166-1 assigned countries. EU/UN are unions, the rest are
 * territories reserved for other standards.
 */
const NOT_COUNTRIES = new Set([
  'EU', 'EZ', 'UN', 'QO', 'XA', 'XB', 'ZZ',
  'AC', 'CP', 'DG', 'EA', 'IC', 'TA',
])

/**
 * Is this the code ISO currently uses for that country?
 *
 * ICU still resolves superseded codes, and it resolves them to the SAME display
 * name as their replacement: UK and GB are both "United Kingdom", SU and RU are
 * both "Russia", CS and YU and RS are all "Serbia". The list above only caught
 * aggregates, so the dropdown showed Serbia three times and fourteen other
 * countries twice.
 *
 * That is not cosmetic. The stored value is the CODE and demo_country keys the
 * crisis resource lookup. A participant in the UK picking the second "United
 * Kingdom" stores UK; crisis_resources.json is keyed on GB; so at the moment
 * they cross a safety threshold they get the default helpline list instead of
 * their own. A duplicate row in a dropdown becomes the wrong number on a crisis
 * screen.
 *
 * Intl canonicalises deprecated region subtags, so the platform can answer this
 * itself and no list is typed by hand: und-UK canonicalises to und-GB, while
 * und-GB stays und-GB.
 */
function isCurrentIsoCode(code) {
  try {
    return new Intl.Locale(`und-${code}`).region === code
  } catch {
    return false
  }
}

let countryCache = null

/** @returns {Array<{value: string, label: string}>} alpha-2 code -> display name */
export function countryOptions(locale = 'en') {
  if (countryCache) return countryCache
  if (typeof Intl === 'undefined' || typeof Intl.DisplayNames !== 'function') return []

  let names
  try {
    names = new Intl.DisplayNames([locale], { type: 'region' })
  } catch {
    return []
  }

  const out = []
  const seenLabel = new Map()

  for (let a = 65; a <= 90; a += 1) {
    for (let b = 65; b <= 90; b += 1) {
      const code = String.fromCharCode(a, b)
      if (NOT_COUNTRIES.has(code)) continue
      if (!isCurrentIsoCode(code)) continue // superseded code, e.g. UK for GB
      let label
      try {
        label = names.of(code)
      } catch {
        continue
      }
      // ICU returns the code back when it knows no such region.
      if (!label || label === code) continue

      // Second net. If ICU ever resolves two live codes to one name, the
      // participant must still not be offered the same country twice — one of
      // the two would key the crisis lookup wrongly.
      if (seenLabel.has(label)) continue
      seenLabel.set(label, code)

      out.push({ value: code, label })
    }
  }

  out.sort((x, y) => x.label.localeCompare(y.label))
  countryCache = out
  return out
}

let timezoneCache = null

/** @returns {Array<{value: string, label: string}>} IANA id -> readable id */
export function timezoneOptions() {
  if (timezoneCache) return timezoneCache
  let zones = []
  try {
    if (typeof Intl.supportedValuesOf === 'function') zones = Intl.supportedValuesOf('timeZone')
  } catch {
    zones = []
  }
  timezoneCache = zones.map((zone) => ({ value: zone, label: zone.replace(/_/g, ' ') }))
  return timezoneCache
}

/** The device's own zone, which the source says to infer and let them confirm. */
export function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    return ''
  }
}

/**
 * Every field's options as {value, label}.
 *
 * config's demographics options are plain strings where the stored value IS the
 * label. Country and timezone carry options_source: "client" and no list.
 */
export function optionsForField(field) {
  if (Array.isArray(field?.options) && field.options.length) {
    return field.options.map((o) =>
      typeof o === 'string' ? { value: o, label: o } : { value: o.value, label: o.label },
    )
  }
  if (field?.type === 'country') return countryOptions()
  if (field?.type === 'timezone') return timezoneOptions()
  return []
}
